<script setup lang="ts">
import { CircleCheck, Edit } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  EmailStyleTemplate,
  EmailStyleTemplateDto,
  SysOrgRoleName,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth, emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";
import FormResponseTemplate from "../pages/FormResponseTemplate.vue";
const myInfoStore = useMyInfoStore();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updateEmailStyleTemplate", styleTemplate: EmailStyleTemplateDto): void;
  (e: "deleted", emailTemplate: EmailStyleTemplateDto): void;
}>();

const props = defineProps<{
  emailTemplateProp: EmailStyleTemplateDto;
}>();
const emailStyleTemplate = ref<EmailStyleTemplate>();
const emailStyleTemplateDto = ref<EmailStyleTemplateDto>();

const drawerOpen = ref(false);

const editing = ref(false);

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

// Only users with the setup basic role or the user who created the template
// can edit the template name
const canEditTemplateName = computed(() => {
  return (
    hasSetupBasicRole ||
    emailStyleTemplate.value?.sysOrgGuid === myInfoStore.currentSysOrgGuid
  );
});

const showTemplate = async (templateToShow: EmailStyleTemplateDto) => {
  drawerOpen.value = true;
  if (!templateToShow.guid) {
    showError("Email style template not found");
    return;
  }
  await apiClient.emailTemplates
    .getEmailStyleTemplate(templateToShow.guid, myInfoStore.currentSysOrgGuid)
    .then((dto) => {
      emailStyleTemplateDto.value = {
        ...dto,
        updatedAt: dto.updatedAt ?? "",
      };
      emailStyleTemplate.value = dto;
    })
    .catch(showError);
};

const updateEmailStyleTemplate = async () => {
  if (!canEditTemplateName.value) {
    showError("You do not have permission to update email templates");
    return;
  }
  if (!emailStyleTemplate.value?.guid) {
    showError("Email style template not found");
    return;
  }

  await apiClient.emailTemplates
    .updateEmailStyleTemplate(
      emailStyleTemplate.value.guid,
      emailStyleTemplate.value,
    )
    .then((res) => {
      ElMessage.success("Template updated");
      editing.value = false;

      emit("updateEmailStyleTemplate", res);
    })
    .catch(showError);
};

const handleDeleteStyleTemplate = async (
  styleTemplateGuid: string,
  sysOrgGuid: string,
) => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this style template?",
    "Delete Style Template",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!styleTemplateGuid) {
        return;
      }
      await apiClient.emailTemplates
        .deleteEmailStyleTemplate(styleTemplateGuid, sysOrgGuid)
        .then((res) => {
          ElMessage.success("Style template deleted");
          emit("closeDrawer");
          emit("deleted", res);
        })
        .catch(showError);
    })
    .catch(async () => {
      // do nothing
    });
};

const handleClose = async () => {
  emit("closeDrawer");
};

onMounted(async () => {
  await showTemplate(props.emailTemplateProp);
});
</script>

<template>
  <el-drawer
    v-if="emailStyleTemplate?.guid"
    :model-value="drawerOpen"
    :title="emailStyleTemplate.name || '\u00A0'"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
    class="user-drawer email-style-template-drawer"
    @closed="handleClose"
  >
    <template #header>
      <div v-if="editing && canEditTemplateName" class="el-drawer__title">
        <h2>
          <el-input
            v-model="emailStyleTemplate.name"
            placeholder="Email Style Template Name"
            :style="{ width: '300px' }"
            @keyup.enter="updateEmailStyleTemplate"
          />
        </h2>

        <el-button
          class="el-drawer__close-btn"
          :icon="CircleCheck"
          size="large"
          @click="updateEmailStyleTemplate"
        ></el-button>
      </div>
      <div v-else class="el-drawer__title withEntity">
        <div class="template-info">
          <h2>
            {{ emailStyleTemplate.name }}
          </h2>
          <el-button
            v-if="canEditTemplateName"
            class="el-drawer__close-btn"
            :icon="Edit"
            size="large"
            @click="editing = true"
          ></el-button>
        </div>
      </div>
    </template>

    <FormResponseTemplate
      v-if="emailStyleTemplate.formDefinitionGuid"
      class="form"
      :form-guid="emailStyleTemplate.formDefinitionGuid"
      :response-guid="emailStyleTemplate.formResponseGuid"
    />
    <el-button
      v-if="emailStyleTemplate.guid !== emptyGuid"
      type="danger"
      plain
      @click="
        handleDeleteStyleTemplate(
          emailStyleTemplate.guid,
          myInfoStore.currentSysOrgGuid,
        )
      "
      >Delete Template</el-button
    >
  </el-drawer>
</template>

<style lang="less">
.email-style-template-drawer {
  .el-drawer__body {
    overflow-y: auto;
  }
  .el-drawer__header {
    .el-drawer__title {
      display: flex;
      align-items: center;
      .el-input__inner {
        font-size: 1.2rem;
      }

      .template-info {
        display: flex;
        align-items: center;
      }

      &.withEntity {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-right: 11px;
        .entity-info {
          margin-left: 15px;
          cursor: pointer;
          font-size: 1.5rem;
        }
      }
    }
  }
  .form {
    margin-top: 0.5em;
    .viewersBar {
      padding-bottom: 0;
    }
  }
  .archive-btn {
    position: absolute;
    bottom: 1em;
    right: 1em;
    color: #afafaf;
  }

  .template-info {
    display: flex;
    gap: 1em;

    table {
      width: 100%;
      td {
        padding: 0.5em;
      }
    }
  }

  .summary-section {
    display: grid;
    grid-template-columns: auto auto;

    .summary-info {
      display: flex;
      flex-direction: row;
      gap: 0.5em;
      flex-wrap: wrap;
      margin-bottom: 1em;
      .summary-info-row {
        display: flex;
        align-items: center;
        gap: 2em;
        flex-wrap: wrap;
        text-wrap: nowrap;

        .sub-header {
          font-weight: 600;
        }
      }
    }
  }

  .el-collapse-item__header {
    font-size: 1.15em;
    border-radius: 10px;
    padding: 0.3rem 1rem;
    background-color: rgba(229, 229, 229, 0.598);
    box-shadow: inset 0 -1.5px 0 rgba(0, 0, 0, 0.1);
    line-height: normal;
    height: fit-content;
    color: inherit;
    font-weight: inherit;
  }

  .el-collapse-item {
    &.is-active {
      .el-collapse-item__header {
        background-color: var(--el-color-primary-light-7);
      }
    }
  }
}
</style>
